import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  useAuth,
  useGymId,
  useRealtimeSync,
  useRole,
  useStaffPermissions,
  type StaffPermissions,
} from '@structural-climbing/auth'
import { Button, Card, CardBody, Chip } from '@structural-climbing/ui'
import { useGymContext } from '../contexts/GymContext'
import { MemberIdentity } from '../features/members/MemberIdentity'
import { MemberProfileModal } from '../features/members/MemberProfileModal'
import { ChildProfileModal } from '../features/members/ChildProfileModal'
import { GymSetupChecklist } from '../features/onboarding/GymSetupChecklist'
import type { PaymentMethod } from '../features/invoices/paymentMethod'
import { PageHeader } from '../components/PageHeader'
import { StatTile } from '../components/StatTile'
import { ListCard } from '../components/ListCard'

// The board shows who is in the gym right now, so it has to move on its own
// as check-ins happen. Only check_ins: the app role cannot read guest_check_ins
// or subscriptions, and Realtime refuses a filter on a column the role cannot
// read, which fails the whole channel ("invalid column for filter gym_id").
// Guest check-ins and plan sales show on the next foreground or 60 s re-read.
const CHECK_IN_BINDINGS = (gymId: string | null | undefined) =>
  gymId ? [{ table: 'check_ins', filter: `gym_id=eq.${gymId}` }] : []

interface DashboardStats {
  today_check_ins: number
  adult_members: number
  child_members: number
  active_subscriptions: number
  depleted_in_gym: number
  daily_first_timers: number
  daily_new_subscriptions: number
  daily_income: number
  // Absent until the 20260925000003 migration reaches the database; the card
  // then shows the total alone.
  daily_income_by_method?: Record<PaymentMethod, number>
  /** Today's online sales after Stripe's fee and ours. */
  daily_online_net?: number
  daily_income_currency: string
  /**
   * Partners that pay per visit (ESX, 7card...), one card each. Not part of
   * daily_income. Absent from a Worker that predates them.
   */
  external_providers?: { provider: string; check_ins: number; income: number }[]
}

// Desk methods first, in the order staff count them at close.
const INCOME_METHODS: PaymentMethod[] = ['cash', 'card', 'transfer', 'online']

interface ActiveCheckIn {
  id: string
  user_id: string | null
  /** Set when a parent checked their child in; the row is the child's. */
  child_id?: string | null
  member_name: string
  nickname: string | null
  email: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
  checked_in_at: string
  is_guest: boolean
  flags?: Record<string, boolean>
  /** The parent or guardian to call, for someone under 18. */
  guardian?: { name: string; phone: string | null } | null
}

// Each card opens the screen behind its number, already filtered so the list
// matches the count. Staff only get a link where RequirePermission would let
// them through; otherwise the card stays a plain number.
function useCanOpen() {
  const role = useRole()
  const staffPermissions = useStaffPermissions()
  return (key: keyof StaffPermissions) => role === 'gym_owner' || staffPermissions?.[key] === true
}

export function DashboardPage() {
  const { isAuthenticated, user, loginWithRedirect, getAccessTokenSilently } = useAuth()
  const gymId = useGymId()
  const { t, i18n } = useTranslation()
  const { timezone, slug } = useGymContext()
  const canOpen = useCanOpen()
  const linkTo = (key: keyof StaffPermissions, path: string) =>
    canOpen(key) ? `/${slug}/${path}` : null
  const incomeLink = linkTo('invoices', 'invoices?date=today')
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [activeCheckIns, setActiveCheckIns] = useState<ActiveCheckIn[]>([])
  const [checkingOut, setCheckingOut] = useState<string | null>(null)
  const [alreadyCheckedOutId, setAlreadyCheckedOutId] = useState<string | null>(null)
  const [profileUserId, setProfileUserId] = useState<string | null>(null)
  const [childProfileId, setChildProfileId] = useState<string | null>(null)

  const loadStats = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const [statsRes, activeRes] = await Promise.all([
        fetch(`${apiUrl}/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/active-check-ins`, { headers: { Authorization: `Bearer ${token}` } }),
      ])
      if (statsRes.ok) {
        setStats((await statsRes.json()) as DashboardStats)
      }
      if (activeRes.ok) {
        const data = (await activeRes.json()) as { active_check_ins: ActiveCheckIn[] }
        setActiveCheckIns(data.active_check_ins)
      }
    } catch {
      // non-critical
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isAuthenticated) void loadStats()
  }, [isAuthenticated, loadStats])

  useRealtimeSync({
    channel: gymId ? `gym-checkins-${gymId}` : null,
    bindings: CHECK_IN_BINDINGS(gymId),
    onChange: loadStats,
    onResync: loadStats,
  })

  async function handleCheckOut(checkInId: string, isGuest: boolean) {
    setCheckingOut(checkInId)
    setAlreadyCheckedOutId(null)
    try {
      const token = await getAccessTokenSilently()
      const endpoint = isGuest ? `guest-check-out/${checkInId}` : `check-out/${checkInId}`
      const res = await fetch(`${apiUrl}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.status === 409) {
        setAlreadyCheckedOutId(checkInId)
      }
      await loadStats()
    } catch {
      // non-critical
    } finally {
      setCheckingOut(null)
    }
  }

  const byMethod = stats?.daily_income_by_method

  if (!isAuthenticated) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
        <h1 className="text-2xl font-bold">{t('dashboard.gymPortalTitle')}</h1>
        <p className="text-foreground/60">{t('dashboard.signInPrompt')}</p>
        <Button color="primary" onClick={() => loginWithRedirect()}>
          {t('common.signIn')}
        </Button>
      </div>
    )
  }

  const todayLabel = new Intl.DateTimeFormat(i18n.language, {
    timeZone: timezone ?? undefined,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date())
  const timeOf = (iso: string) =>
    new Intl.DateTimeFormat(undefined, {
      timeZone: timezone ?? undefined,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('dashboard.title')} subtitle={todayLabel} />
      <p className="-mt-2 mb-6 text-foreground/60">
        {t('dashboard.welcomeBack', { name: user?.name ?? user?.email })}
      </p>
      <GymSetupChecklist />

      <div className="mb-3 flex flex-wrap items-baseline gap-x-2">
        <h2 className="text-base font-semibold">{t('dashboard.dailyTitle')}</h2>
        <p className="text-sm text-foreground/50">{t('dashboard.dailySubtitle')}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          to={linkTo('check_in', 'check-in?tab=history')}
          label={t('dashboard.checkInsToday')}
          value={stats ? stats.today_check_ins : '—'}
        />
        <StatTile
          to={linkTo('check_in', 'check-in?tab=history&first=1')}
          label={t('dashboard.dailyFirstTimers')}
          value={stats ? stats.daily_first_timers : '—'}
          hint={stats && t('dashboard.dailyFirstTimersHint')}
        />
        <StatTile
          to={linkTo('subscriptions', 'subscriptions?view=detail&status=all&sold=today')}
          label={t('dashboard.dailyNewSubscriptions')}
          value={stats ? stats.daily_new_subscriptions : '—'}
          hint={stats && t('dashboard.dailyNewSubscriptionsHint')}
        />
        {/* Not one pressable card: each method row is its own link to that
            method's sales, and links cannot nest inside a button. */}
        <Card shadow="sm" className="col-span-2 max-lg:order-last lg:col-span-1">
          <CardBody className="gap-0.5 px-4 py-3.5">
            <MaybeLink to={incomeLink} className="block">
              <p className="text-sm text-foreground/60">{t('dashboard.dailyIncome')}</p>
              <p className="text-2xl font-bold tabular-nums tracking-tight sm:text-3xl">
                {stats ? formatMoney(stats.daily_income, stats.daily_income_currency) : '—'}
              </p>
            </MaybeLink>
            {byMethod && stats && (
              <ul className="mt-1.5 flex flex-col gap-0.5 text-xs">
                {INCOME_METHODS.map((method) => (
                  <li key={method}>
                    <MaybeLink
                      to={incomeLink && `${incomeLink}&payment=${method}`}
                      className="flex justify-between gap-2"
                    >
                      <span className="text-foreground/50">{t(`invoices.${method}`)}</span>
                      <span className="font-medium tabular-nums">
                        {formatMoney(byMethod[method], stats.daily_income_currency)}
                      </span>
                    </MaybeLink>
                  </li>
                ))}
              </ul>
            )}
            {/* The headline stays gross, because that is the revenue and what
                the receipts say. An online sale is the one method where the
                fees are known to the leu, so the amount that will reach the
                bank is worth saying out loud rather than leaving to Stripe. */}
            {byMethod && stats && byMethod.online > 0 && stats.daily_online_net !== undefined && (
              <p className="mt-1 text-xs text-foreground/55">
                {t('dashboard.dailyOnlineNet', {
                  amount: formatMoney(stats.daily_online_net, stats.daily_income_currency),
                })}
              </p>
            )}
          </CardBody>
        </Card>
        {/* On a phone: a fourth small card, with income across the row below.
            On a wide screen: its own row. No separate Active subscriptions
            card: it showed the same count. */}
        <div className="lg:col-span-4">
          <StatTile
            to={linkTo('members', 'members?status=active')}
            label={t('dashboard.totalActiveMembers')}
            value={stats ? stats.adult_members + stats.child_members : '—'}
            hint={
              stats &&
              t('dashboard.membersBreakdown', {
                adults: stats.adult_members,
                children: stats.child_members,
              })
            }
          >
            {stats && stats.depleted_in_gym > 0 && (
              <p className="text-xs text-warning-600">
                {t('dashboard.depletedInGym', { count: stats.depleted_in_gym })}
              </p>
            )}
          </StatTile>
        </div>
      </div>

      {/* One card per partner: today's visits, and what it owes for them. */}
      {stats && (stats.external_providers ?? []).length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {(stats.external_providers ?? []).map((p) => (
            <StatTile
              key={p.provider}
              to={linkTo('statistics', 'statistics')}
              label={t('dashboard.externalCheckIns', { provider: p.provider })}
              value={p.check_ins}
              hint={t('dashboard.externalIncome', {
                amount: formatMoney(p.income, stats.daily_income_currency),
              })}
            />
          ))}
        </div>
      )}

      <div className="mt-6">
        <ListCard
          title={t('dashboard.currentlyInGym')}
          count={activeCheckIns.length}
          items={activeCheckIns}
          getKey={(ci) => ci.id}
          initialCount={5}
          emptyText={t('dashboard.noMembersCheckedIn')}
          renderItem={(ci) => {
            const canOpenProfile = Boolean(ci.user_id) && !ci.is_guest
            return (
              <div className="flex items-center gap-3">
                <button
                  className={`flex min-w-0 flex-1 items-center text-left${canOpenProfile ? ' cursor-pointer' : ''}`}
                  onClick={() => {
                    if (!ci.user_id || ci.is_guest) return
                    // A child opens their own profile, as on Subscriptions
                    // and Members, not the parent's.
                    if (ci.child_id) setChildProfileId(ci.child_id)
                    else setProfileUserId(ci.user_id)
                  }}
                  disabled={!canOpenProfile}
                >
                  <MemberIdentity
                    userId={ci.user_id}
                    name={ci.member_name}
                    nickname={ci.nickname}
                    email={ci.email}
                    avatarExt={ci.avatar_ext}
                    avatarUpdatedAt={ci.avatar_updated_at}
                    size="md"
                    isClubMember={ci.flags?.is_club_member}
                    subtitle={
                      <>
                        {t('dashboard.checkedInAt', { time: timeOf(ci.checked_in_at) })}
                        {ci.guardian && (
                          <span className="block text-foreground/70">
                            {t('dashboard.guardianLine', { name: ci.guardian.name })}
                            {ci.guardian.phone && ` · ${ci.guardian.phone}`}
                          </span>
                        )}
                      </>
                    }
                  />
                </button>
                {ci.is_guest && (
                  <Chip size="sm" variant="flat" color="secondary" className="shrink-0">
                    {t('dashboard.guest')}
                  </Chip>
                )}
                {alreadyCheckedOutId === ci.id ? (
                  <Chip size="sm" variant="flat" color="warning" className="shrink-0">
                    {t('dashboard.alreadyCheckedOut')}
                  </Chip>
                ) : (
                  <Button
                    size="sm"
                    variant="bordered"
                    className="shrink-0 border-1"
                    isDisabled={checkingOut === ci.id}
                    onPress={() => void handleCheckOut(ci.id, ci.is_guest)}
                  >
                    {checkingOut === ci.id ? t('dashboard.checkingOut') : t('dashboard.checkOut')}
                  </Button>
                )}
              </div>
            )
          }}
        />
      </div>

      {profileUserId && (
        <MemberProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} />
      )}
      {childProfileId && (
        <ChildProfileModal
          childId={childProfileId}
          onClose={() => setChildProfileId(null)}
          onOpenParent={(parentId) => {
            setChildProfileId(null)
            setProfileUserId(parentId)
          }}
        />
      )}
    </div>
  )
}

function MaybeLink({
  to,
  className,
  children,
}: {
  to: string | null
  className: string
  children: ReactNode
}) {
  if (!to) return <div className={className}>{children}</div>
  return (
    <Link to={to} className={`${className} rounded-md hover:bg-foreground/5`}>
      {children}
    </Link>
  )
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
}
